(() => {
  'use strict';
  if (customElements.get('nordic-cinema')) return;
  class NordicCinema extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-cinema-motion]');
      this.panels = [...this.querySelectorAll('[data-cinema-scene]')];
      if (!this.video || !this.button || this.panels.length !== 3) return;
      this.lifecycle = new AbortController();
      const on = (el, type, fn) => el?.addEventListener(type, fn, {signal:this.lifecycle.signal});
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.pausedByUser = this.reduced.matches;
      this.inView = false;
      this.active = -1;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playbackRate = .8;
      this.button.hidden = false;
      on(this.button, 'click', () => {
        this.pausedByUser = !this.video.paused;
        if (!this.pausedByUser && this.masterPaused()) this.master?.click();
        this.playback();
      });
      on(this.video, 'play', () => { this.syncButton(); this.requestFrame(); });
      on(this.video, 'pause', () => { this.cancelFrame(); this.syncButton(); this.render(); });
      on(this.video, 'loadeddata', () => this.playback());
      on(this.video, 'timeupdate', () => this.render());
      on(this.video, 'seeked', () => this.render());
      on(this.video, 'error', () => {
        this.button.disabled = true;
        this.button.querySelector('span').textContent = this.button.dataset.error;
      });
      on(document, 'visibilitychange', () => this.playback());
      on(this.reduced, 'change', e => { this.pausedByUser = e.matches; this.playback(); });
      on(this, 'focusin', e => {
        if (e.target.closest('.nordic-cinema__cta') && e.target.matches(':focus-visible')) {
          this.pausedByUser = true; this.playback();
        }
      });
      on(document, 'shopify:section:load', () => this.bindMaster());
      on(window, 'pagehide', () => this.video.pause());
      on(window, 'pageshow', () => this.playback());
      this.visibility = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.playback();
      }, {threshold:.1});
      this.visibility.observe(this);
      this.preloadObserver = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        this.preloadObserver.disconnect();
        if (this.video.readyState === 0 && this.video.paused) {
          this.video.preload = 'auto'; this.video.load();
        }
      }, {rootMargin:'350px'});
      this.preloadObserver.observe(this);
      this.bindMaster();
      this.render();
      this.syncButton();
    }
    bindMaster() {
      this.masterObserver?.disconnect();
      this.master = document.getElementById('nordic-motionToggle');
      if (this.master) {
        this.masterObserver = new MutationObserver(() => this.playback());
        this.masterObserver.observe(this.master, {attributes:true,attributeFilter:['aria-pressed']});
      }
      this.playback();
    }
    masterPaused() { return this.master?.getAttribute('aria-pressed') === 'true'; }
    playback() {
      if (!this.lifecycle) return;
      if (this.inView && !document.hidden && !this.pausedByUser && !this.masterPaused()) {
        this.video.play().catch(() => this.syncButton());
      } else this.video.pause();
      this.syncButton();
      this.render();
    }
    syncButton() {
      const playing = !this.video.paused;
      this.button.querySelector('span').textContent = playing ? this.button.dataset.pause : this.button.dataset.play;
      this.button.querySelector('path').setAttribute('d', playing ? 'M7 4v12M13 4v12' : 'm7 4 8 6-8 6Z');
    }
    render() {
      const time = this.video.currentTime;
      const scene = time < 8.8 ? 0 : time < 19.8 ? 1 : time < 28.3 ? 2 : 0;
      if (scene !== this.active) {
        this.active = scene;
        this.dataset.scene = String(scene);
        this.panels.forEach((panel, i) => { panel.hidden = i !== scene; });
      }
      const opacity = this.video.paused || this.reduced.matches ? 1 : Math.min(1, ...[8.8,19.8,28.3].map(cue => Math.abs(time-cue)/.45));
      this.panels[this.active].style.opacity = String(opacity);
    }
    requestFrame() {
      if (this.frame != null || this.video.paused || document.hidden) return;
      const callback = () => { this.frame = null; this.render(); this.requestFrame(); };
      this.frame = this.video.requestVideoFrameCallback ? this.video.requestVideoFrameCallback(callback) : requestAnimationFrame(callback);
    }
    cancelFrame() {
      if (this.frame == null) return;
      if (this.video.cancelVideoFrameCallback) this.video.cancelVideoFrameCallback(this.frame);
      else cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    disconnectedCallback() {
      if (!this.lifecycle) return;
      this.lifecycle.abort();
      this.lifecycle = null;
      this.video.pause();
      this.cancelFrame();
      this.visibility?.disconnect();
      this.preloadObserver?.disconnect();
      this.masterObserver?.disconnect();
    }
  }
  customElements.define('nordic-cinema', NordicCinema);
})();
